import { isProductionData, listSiteSections, listSiteContent, saveSiteSection, saveSiteContent } from '../lib/repository';
import { toast } from '../components/toast';
import type { SiteSection, SiteCourseListing } from '../site-content';

type SiteTab = 'page' | 'hero' | 'batchMeta' | 'math' | 'faq' | 'trainer' | 'testimonials';

const TABS: Array<{ id: SiteTab; label: string }> = [
  { id: 'page', label: 'Course page' },
  { id: 'hero', label: 'Hero' },
  { id: 'batchMeta', label: 'Program' },
  { id: 'math', label: 'The Math' },
  { id: 'faq', label: 'FAQs' },
  { id: 'trainer', label: 'Trainer' },
  { id: 'testimonials', label: 'Testimonials' },
];

// Field schema — keys MUST match site-bridge.js + the data-tt attributes on techtatwa.in
interface Field { key: string; label: string; kind: 'text' | 'textarea' | 'number' | 'list'; hint?: string }
const SCHEMA: Record<Exclude<SiteTab, 'page'>, Field[]> = {
  hero: [
    { key: 'headline', label: 'Headline', kind: 'text' },
    { key: 'subline', label: 'Subline', kind: 'textarea' },
    { key: 'badge', label: 'Badge (e.g. Batch 01 · seats 35–50)', kind: 'text' },
  ],
  batchMeta: [
    { key: 'price', label: 'Program fee (₹)', kind: 'number' },
    { key: 'durationLabel', label: 'Duration label', kind: 'text' },
    { key: 'durationWeeks', label: 'Duration (weeks)', kind: 'number' },
    { key: 'launchDate', label: 'Launch date (ISO)', kind: 'text', hint: 'e.g. 2026-09-15' },
    { key: 'launchLabel', label: 'Launch label', kind: 'text' },
    { key: 'enrollClose', label: 'Enrolment close (ISO)', kind: 'text' },
    { key: 'enrollLabel', label: 'Enrolment close label', kind: 'text' },
    { key: 'seats', label: 'Seats', kind: 'text' },
    { key: 'batchLabel', label: 'Batch label', kind: 'text' },
  ],
  math: [
    { key: 'counter1', label: 'Counter 1 — value', kind: 'number' },
    { key: 'counter1Label', label: 'Counter 1 — label', kind: 'text' },
    { key: 'counter2', label: 'Counter 2 — value', kind: 'number' },
    { key: 'counter2Label', label: 'Counter 2 — label', kind: 'text' },
    { key: 'counter3', label: 'Counter 3 — value', kind: 'number' },
    { key: 'counter3Label', label: 'Counter 3 — label', kind: 'text' },
  ],
  faq: [{ key: 'items', label: 'FAQ items (one per line: Question | Answer)', kind: 'list' }],
  trainer: [
    { key: 'name', label: 'Name', kind: 'text' },
    { key: 'role', label: 'Role', kind: 'text' },
    { key: 'bio', label: 'Bio', kind: 'textarea' },
    { key: 'photoUrl', label: 'Photo URL', kind: 'text' },
  ],
  testimonials: [{ key: 'items', label: 'Testimonials (one per line: Who | Quote)', kind: 'list' }],
};

const SEED: Record<Exclude<SiteTab, 'page'>, Omit<SiteSection, 'id' | 'tenantId' | 'updatedAt'>> = {
  hero: { type: 'hero', title: 'Home hero', status: 'published', body: { headline: 'Stop studying business. Start one.', subline: 'An 8-week entrepreneurship incubator where students launch a real, GST-registered e-commerce brand — with a safety net.', badge: 'Batch 01 · seats 35–50' } },
  batchMeta: { type: 'batchMeta', title: 'Program & batch', status: 'published', body: { price: 5000, durationWeeks: 8, durationLabel: '8 weeks · 60 days', launchDate: '2026-09-15', launchLabel: '15 September 2026', enrollClose: '2026-09-10', enrollLabel: 'Enrolment closes 10 September', seats: '35–50', batchLabel: 'Batch 01' } },
  math: { type: 'math', title: 'The Math', status: 'published', body: { counter1: 2200, counter1Label: 'Average student payout (₹/mo)', counter2: 800, counter2Label: 'Meta ad budget to start (₹)', counter3: 5000, counter3Label: 'Program fee (₹)' } },
  faq: { type: 'faq', title: 'FAQs', status: 'published', body: { items: ['What is the batch size? | 35–50 students, hands-on with live orders.|Do students need money to start? | No — inventory is funded and risk-managed for the cohort.|Is it safe for parents? | There is a built-in loss safety net: ₹5,000 fee covers a funded store, not unchecked ad spend.'] } },
  trainer: { type: 'trainer', title: 'Founder & trainer', status: 'published', body: { name: 'Shubham Gaur', role: 'Founder & CEO', bio: 'Built Tech Tatwa to turn classrooms into real storefronts — pairing entrepreneurship education with e-commerce actually run by students.', photoUrl: '' } },
  testimonials: { type: 'testimonials', title: 'Testimonials', status: 'published', body: { items: ['A parent: "I was nervous about ads, but the safety net made it feel low-risk." | Verified parent, Batch 00'] } },
};

let sections: SiteSection[] | null = null;
let pages: SiteCourseListing[] | null = null;
let loading = false;

function load(): void {
  if (!isProductionData() || loading) return;
  if (sections !== null && pages !== null) return;
  loading = true;
  Promise.all([listSiteSections().catch(() => []), listSiteContent().catch(() => [])])
    .then(([secs, pgs]) => { sections = secs; pages = pgs as SiteCourseListing[]; loading = false; (window as any).__rerender?.(); refreshSeed(); })
    .catch(() => { loading = false; });
}

// Auto-create missing section templates on first open so the editor always has content.
let seeded = false;
function refreshSeed(): void {
  if (seeded || !sections) return;
  seeded = true;
  const ids = new Set(sections.map((s) => s.id));
  let missing: Array<Exclude<SiteTab, 'page'>> = [];
  (Object.keys(SEED) as Array<Exclude<SiteTab, 'page'>>).forEach((t) => { if (!ids.has(t)) missing.push(t); });
  if (missing.length) {
    missing.forEach((t) => {
      const s = SEED[t as Exclude<SiteTab, 'page'>];
      void saveSiteSection({ ...s, id: t, tenantId: 'techtatwa', updatedAt: new Date().toISOString() }, t)
        .then(() => { sections?.push({ ...s, id: t, tenantId: 'techtatwa', updatedAt: new Date().toISOString() }); (window as any).__rerender?.(); })
        .catch(() => {});
    });
    toast('Seeded ' + missing.length + ' missing section(s) from template', 'success');
  }
}

export function renderSiteContent(): string {
  load();
  const activeRaw = (location.hash.match(/tab=([a-z-]+)/) || [])[1] || 'page';
  const active: SiteTab = TABS.some((t) => t.id === activeRaw) ? (activeRaw as SiteTab) : 'page';
  const nav = '<nav class="seg-nav" style="flex-wrap:wrap">' + TABS.map((t) =>
    '<button data-action="site-tab" data-tab="' + t.id + '" class="seg-item ' + (t.id === active ? 'active' : '') + '">' + t.label + '</button>').join('') + '</nav>';
  const body = active === 'page' ? renderPagePanel() : renderSectionPanel(active);
  // Universal snippet the user pastes once into techtatwa.in <head>. The bridge
  // is hosted on clientale.in so we don't depend on FTP-deploy to MilesWeb.
  const apiKey = (window as any).__firebaseApiKey || 'YOUR_FB_API_KEY';
  const bridgeSnippet =
    '<!-- Clientale ↔ techtatwa.in bridge — paste once in <head>, before </body> ideally -->\n' +
    '<script>window.TT_BRIDGE={apiKey:"' + apiKey + '",projectId:"clientale313",tenantId:"techtatwa",bridgeSrc:"https://clientale.in/site-bridge.js"};</script>\n' +
    '<script async src="https://clientale.in/site-bridge.js"></script>';
  const bridgePanel =
    '<div class="card card-pad-lg" style="margin-top:14px;border-left:4px solid var(--accent)">' +
      '<div class="row" style="gap:10px;align-items:center;margin-bottom:8px"><div class="t-h2" style="flex:1">Connect your live site</div><span class="pill pill-warn"><span class="dot"></span>One-time install</span></div>' +
      '<p class="t-meta" style="margin-bottom:12px">Paste this snippet into <code>&lt;head&gt;</code> of every page on <strong>techtatwa.in</strong> (WordPress → theme header.php, or a static site → each HTML file). The bridge reads <code>sections</code> and <code>siteContent</code> from your Clientale Firestore — no FTP deploy needed.</p>' +
      '<textarea readonly rows="6" class="form-input" style="width:100%;font-family:JetBrains Mono,monospace;font-size:11px">' + bridgeSnippet.replace(/</g, '&lt;') + '</textarea>' +
      '<div class="row" style="gap:10px;margin-top:10px">' +
        '<button type="button" class="btn btn-primary" data-copy-bridge>Copy snippet</button>' +
        '<a class="btn btn-ghost" href="https://clientale.in/site-bridge.js" target="_blank" rel="noopener">Test bridge (open)</a>' +
      '</div></div>';
  return (
    '<div class="screen-head"><div><div class="t-eyebrow">Website admin</div><h1 class="t-h1">Your public site</h1>' +
    '<p class="t-meta">Edits publish instantly to ' + (getTenantName() || 'techtatwa.in') + ' — the site bridge reads these sections live, no rebuild.</p></div></div>' +
    '<div class="card" style="padding:14px;margin:14px 0"><div class="row" style="gap:8px;flex-wrap:wrap"><span class="t-meta">How it works</span>' +
    '<span class="pill pill-success"><span class="dot live"></span> Publish → live in ~1s</span>' +
    '<span class="pill pill-info">Sections carry exact keys the techtatwa.in bridge reads</span></div></div>' +
    bridgePanel +
    nav + '<section class="card card-pad-lg" style="margin-top:14px">' + (loading ? '<div class="t-meta">Loading site content…</div>' : body) + '</section>'
  );
}

function getTenantName(): string {
  try { return (window as any).__tenantName || ''; } catch { return ''; }
}

function statusChip(status: string): string {
  return status === 'published' ? '<span class="pill pill-success"><span class="dot live"></span> published</span>' : '<span class="pill pill-info">draft</span>';
}

function renderSectionPanel(type: Exclude<SiteTab, 'page'>): string {
  const sec = (sections || []).find((s) => s.id === type);
  if (!sec) return '<div class="empty">No ' + type + ' section yet — it will be created from template automatically.</div>';
  const fields = SCHEMA[type];
  const inputs = fields.map((f) => {
    const raw = (sec.body || {})[f.key];
    let val = '';
    if (Array.isArray(raw)) val = (raw as string[]).join('\n');
    else if (typeof raw === 'number') val = String(raw);
    else val = String(raw ?? '');
    const tag = f.kind === 'textarea' || f.kind === 'list' ? 'textarea' : 'input';
    const attrs = tag === 'textarea' ? 'rows="' + (f.kind === 'list' ? 5 : 3) + '"' : 'type="' + (f.kind === 'number' ? 'number' : 'text') + '"';
    return '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">' + f.label + (f.hint ? '<span class="t-meta" style="text-transform:none;font-weight:400"> — ' + f.hint + '</span>' : '') +
      '<' + tag + ' name="' + f.key + '" ' + attrs + ' class="form-input" style="width:100%;margin-top:4px">' + (tag === 'textarea' ? escapeHtml(val) : '') + (tag === 'input' ? 'value="' + escapeHtml(val) + '"' : '') + '</' + tag + '>';
  }).join('');
  return (
    '<form data-section-form data-type="' + type + '" style="max-width:720px">' +
      '<div class="row" style="gap:10px;align-items:center;margin-bottom:6px"><div class="t-h2" style="flex:1">' + type + '</div>' + statusChip(sec.status) + '</div>' +
      '<div class="t-meta" style="margin-bottom:8px">' + (sec.title || '') + '</div>' + inputs +
      '<div class="row" style="gap:10px;margin-top:18px">' +
        '<button class="btn btn-primary" type="submit">Publish changes</button>' +
        '<button class="btn btn-ghost" type="button" data-section-status data-target="' + type + '" data-next="' + (sec.status === 'published' ? 'draft' : 'published') + '">' + (sec.status === 'published' ? 'Unpublish' : 'Publish') + '</button>' +
      '</div></form>'
  );
}

function renderPagePanel(): string {
  const page = (pages || [])[0];
  if (!page) return '<div class="empty">No course page yet — it will seed automatically, or save one below.</div>';
  return (
    '<form data-page-form data-id="' + page.id + '" style="max-width:720px">' +
      '<div class="row" style="gap:10px;align-items:center;margin-bottom:6px"><div class="t-h2" style="flex:1">Course page</div>' + statusChip(page.status) + '</div>' +
      '<div class="t-meta" style="margin-bottom:8px">Slug: ' + escapeHtml(page.slug || 'course-page') + '</div>' +
      '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">Title<input name="title" class="form-input" style="width:100%;margin-top:4px" value="' + escapeHtml(page.title) + '"/></label>' +
      '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">Subtitle<input name="subtitle" class="form-input" style="width:100%;margin-top:4px" value="' + escapeHtml(page.subtitle || '') + '"/></label>' +
      '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">Description<textarea name="description" rows="4" class="form-input" style="width:100%;margin-top:4px">' + escapeHtml(page.description || '') + '</textarea></label>' +
      '<div class="row" style="gap:16px"><label style="flex:1" class="t-eyebrow" style="display:block;margin:12px 0 4px">Price (₹)<input type="number" name="price" class="form-input" style="width:100%;margin-top:4px" value="' + (page.price || 0) + '"/></label>' +
      '<label style="flex:1" class="t-eyebrow" style="display:block;margin:12px 0 4px">Duration (weeks)<input type="number" name="durationWeeks" class="form-input" style="width:100%;margin-top:4px" value="' + (page.durationWeeks || 8) + '"/></label></div>' +
      '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">Launch date<input name="launchDate" class="form-input" style="width:100%;margin-top:4px" value="' + escapeHtml(page.launchDate || '') + '"/></label>' +
      '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">Trainer name<input name="trainerName" class="form-input" style="width:100%;margin-top:4px" value="' + escapeHtml(page.trainerName || '') + '"/></label>' +
      '<label class="t-eyebrow" style="display:block;margin:12px 0 4px">Syllabus (one per line)<textarea name="syllabus" rows="6" class="form-input" style="width:100%;margin-top:4px">' + escapeHtml((page.syllabus || []).join('\n')) + '</textarea></label>' +
      '<div class="row" style="gap:10px;margin-top:18px">' +
        '<button class="btn btn-primary" type="submit">Publish changes</button>' +
        '<button class="btn btn-ghost" type="button" data-page-status data-id="' + page.id + '" data-next="' + (page.status === 'published' ? 'draft' : 'published') + '">' + (page.status === 'published' ? 'Unpublish' : 'Publish') + '</button>' +
      '</div></form>'
  );
}

export function bindSiteContent(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const copy = t.closest<HTMLElement>('[data-copy-bridge]');
    if (copy) {
      const textarea = (copy.closest('.card')?.querySelector('textarea') as HTMLTextAreaElement | null);
      if (!textarea) return;
      textarea.select();
      try { document.execCommand('copy'); toast('Snippet copied — paste into techtatwa.in <head>', 'success'); }
      catch { toast('Could not copy — select the text manually', 'warn'); }
      return;
    }
    const tab = t.closest<HTMLElement>('[data-action="site-tab"]');
    if (tab && tab.dataset.tab) { location.hash = '#/site-content?tab=' + tab.dataset.tab; (window as any).__rerender?.(); return; }
    const secToggle = t.closest<HTMLElement>('[data-section-status]');
    if (secToggle && secToggle.dataset.target && secToggle.dataset.next) {
      const type = secToggle.dataset.target as Exclude<SiteTab, 'page'>;
      const sec = (sections || []).find((s) => s.id === type); if (!sec) return;
      void saveSiteSection({ ...sec, status: secToggle.dataset.next as 'published' | 'draft' }, type)
        .then(() => { sec.status = secToggle.dataset.next as 'published' | 'draft'; toast(secToggle.dataset.next === 'published' ? 'Section is live on your site' : 'Section unpublished', 'success'); (window as any).__rerender?.(); })
        .catch((err) => toast(err instanceof Error ? err.message : 'Status change failed', 'warn'));
      return;
    }
    const pageToggle = t.closest<HTMLElement>('[data-page-status]');
    if (pageToggle && pageToggle.dataset.id && pageToggle.dataset.next) {
      const id = pageToggle.dataset.id;
      const page = (pages || []).find((p) => p.id === id); if (!page) return;
      void saveSiteContent({ ...page, status: pageToggle.dataset.next as 'published' | 'draft' }, id)
        .then(() => { page.status = pageToggle.dataset.next as 'published' | 'draft'; toast(pageToggle.dataset.next === 'published' ? 'Course page is live' : 'Course page unpublished', 'success'); (window as any).__rerender?.(); })
        .catch((err) => toast(err instanceof Error ? err.message : 'Status change failed', 'warn'));
      return;
    }
  });

  document.addEventListener('submit', async (e) => {
    const secForm = (e.target as HTMLElement).closest<HTMLFormElement>('[data-section-form]');
    if (secForm && secForm.dataset.type) {
      e.preventDefault();
      const type = secForm.dataset.type as Exclude<SiteTab, 'page'>;
      const sec = (sections || []).find((s) => s.id === type); if (!sec) return;
      const body: Record<string, string | number | string[]> = {};
      for (const f of SCHEMA[type]) {
        const el = secForm.querySelector<HTMLInputElement | HTMLTextAreaElement>('[name="' + f.key + '"]');
        if (!el) continue;
        const text = el.value;
        if (f.kind === 'number') body[f.key] = Number(text) || 0;
        else if (f.kind === 'list') body[f.key] = text.split('\n').map((x) => x.trim()).filter(Boolean);
        else body[f.key] = text;
      }
      try {
        await saveSiteSection({ ...sec, body, status: 'published', updatedAt: new Date().toISOString() }, type);
        sec.body = body; sec.status = 'published';
        toast('Published to your live site', 'success');
        (window as any).__rerender?.();
      } catch (err) { toast(err instanceof Error ? err.message : 'Publish failed', 'warn'); }
      return;
    }
    const pageForm = (e.target as HTMLElement).closest<HTMLFormElement>('[data-page-form]');
    if (pageForm && pageForm.dataset.id) {
      e.preventDefault();
      const id = pageForm.dataset.id;
      const page = (pages || []).find((p) => p.id === id); if (!page) return;
      const fd = new FormData(pageForm);
      const payload: SiteCourseListing = {
        ...page,
        title: String(fd.get('title') || page.title),
        subtitle: String(fd.get('subtitle') || ''),
        description: String(fd.get('description') || ''),
        price: Number(fd.get('price') || 0),
        durationWeeks: Number(fd.get('durationWeeks') || 8),
        launchDate: String(fd.get('launchDate') || ''),
        trainerName: String(fd.get('trainerName') || ''),
        syllabus: String(fd.get('syllabus') || '').split('\n').map((s) => s.trim()).filter(Boolean),
        status: 'published',
      };
      try {
        await saveSiteContent(payload, id);
        Object.assign(page, payload);
        toast('Course page published', 'success');
        (window as any).__rerender?.();
      } catch (err) { toast(err instanceof Error ? err.message : 'Publish failed', 'warn'); }
    }
  });
}

function escapeHtml(s: string): string {
  return String(s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c] as string);
}
